import React, { useContext, useEffect, useState } from 'react';
import { Icon, Label, Button, Modal, Form, Grid } from "semantic-ui-react";
import { ReactSVG } from "react-svg";
import Swal from 'sweetalert2';
import { stateToHTML } from 'draft-js-export-html';
import { EditorState, convertFromHTML, ContentState } from 'draft-js';
import RichTextEditor, { decoratorPlugin } from './RichTextEditor';
import AppContext from '../../../AppContext';
import axiosInstance from '../../../helpers/axiosInstance';
import BrightnessContrast from "../../../assets/images/brightness-contrast.svg";
import icoQuestionMark from "../../../assets/images/ico-question-mark.svg";
import icoQuestionMarkWhite from "../../../assets/images/ico-question-mark-white.svg";
import icoEnvelopeOpen from "../../../assets/images/ico-envelope-open.svg";
import { InternalHelpModel } from '../../../types/InternalHelpDTO';

type FormErrorFields = {
  issue: string;
  happenedBefore: string;
  isUrgent: string;
  workImpact: string;
};

const SwitchTheme: React.FC = () => {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isEditorFocus, setIsEditorFocus] = useState<boolean>(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [formValues, setFormValues] = useState<InternalHelpModel>({} as InternalHelpModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    issue: '',
    happenedBefore: '',
    isUrgent: '',
    workImpact: ''
  });

  const { themeVersion, handleThemeChange, isNavOpen, handleShowNav, userInfo, updateNotificationCount, notificationCount } = useContext(AppContext);
  const isDarkTheme = themeVersion === "black" ? true : false;

  useEffect(() => {
    if (userInfo) {
      fetchNotificationCount();
    }
  }, [userInfo]);

  const fetchNotificationCount = async () => {
    try {
      await axiosInstance.get(`/Dashboard/GetNotificationCount`).then(({ data, status }) => {
        if (status === 200) {
          updateNotificationCount(data);
        }
      })
        .catch((error: any) => {
          console.log(error);
        });
    } catch (error: any) {
      console.log(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: any, field: string) => {
    setFormValues({ ...formValues, [field]: data.value === 1 ? true : false });
  };

  const handleRichEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);

    const currentContent = newEditorState.getCurrentContent();
    const plainText = stateToHTML(currentContent);
    // Update the form field value
    setFormValues(currentFormValues => {
      const updatedFormValues = { ...currentFormValues, issue: plainText };
      return updatedFormValues;
    });
  };

  const handleRichEditorFocus = () => {
    if (!isEditorFocus) {
      const tmpDescription = formValues.issue || '';
      // Convert the plain text content to EditorState and update the RichTextEditor
      const blocksFromHTML = convertFromHTML(tmpDescription);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      const newEditorState = EditorState.createWithContent(contentState, decoratorPlugin());
      setEditorState(newEditorState);
    }
    setIsEditorFocus(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            issue: (formValues?.issue === undefined || !formValues?.issue || formValues?.issue === '<p><br></p>') ? 'Required' : '',
            happenedBefore: !formValues?.happenedBefore ? 'Required' : '',
            isUrgent: formValues?.isUrgent === undefined ? 'Required' : '',
            workImpact: !formValues?.workImpact ? 'Required' : '',
          };
        } else {
          switch (fieldName) {
            case 'issue':
              errors.issue = (value === undefined || !value || value === '<p><br></p>') ? 'Required' : '';
              break;
            case 'happenedBefore':
              errors.happenedBefore = !value ? 'Required' : '';
              break;
            case 'isUrgent':
              errors.isUrgent = value === undefined ? 'Required' : '';
              break;
            case 'workImpact':
              errors.workImpact = !value ? 'Required' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission
      axiosInstance.post('/AdminSetting/SubmitHelpRequest', formValues).then(({ data, status, statusText }) => {
        if (status === 200) {
          setFormValues({} as InternalHelpModel);
          setEditorState(() => EditorState.createEmpty());
          Swal.fire({
            title: 'Email has been sent',
            icon: 'success',
            confirmButtonText: 'Ok',
            showConfirmButton: true,
            showDenyButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then((result) => {
            setIsOpenModal(false);
          });
        }
      });
    }
  };

  const buttonToggle = isNavOpen ? "toggle-nav toggle-nav--active" : "toggle-nav ";

  return (
    <>
      <div className='ui basic button' style={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
        localStorage.setItem('caseDataGridTypeId', '3');
        localStorage.removeItem('dashboardFilter');
        window.location.href = '/dashboard?unread=1'
      }}>
        <Button.Content visible>
          <ReactSVG
            src={icoEnvelopeOpen}
          />
          <Label circular floating style={{ backgroundColor: '#ddd', color: '#000', top: '5%', left: '75%' }}>{notificationCount}</Label>
        </Button.Content>
      </div>
      <a className='ui basic button' onClick={() => setIsOpenModal(true)}>
        <Button.Content visible>
          <ReactSVG
            src={themeVersion === "black" ? icoQuestionMarkWhite : icoQuestionMark}
          />
        </Button.Content>
      </a>
      <Button
        basic
        onClick={() => {
          handleThemeChange();
        }}
      >
        <Button.Content visible>
          <ReactSVG
            src={BrightnessContrast}
          />
        </Button.Content>
      </Button>
      <Button
        basic
        className={buttonToggle}
        onClick={(event: any) =>
          handleShowNav(event)
        }
      >
        <span></span>
        <span></span>
        <span></span>
      </Button>
      <Modal
        onClose={() => setIsOpenModal(false)}
        onOpen={() => setIsOpenModal(true)}
        open={isOpenModal}
        className={themeVersion === 'black' ? 'dark-theme' : ''}
      >
        <Modal.Header>
          <span>Internal Help Form</span>
          <span><Icon name="close" style={{ float: "right", cursor: "pointer" }} onClick={() => setIsOpenModal(false)} /></span>
        </Modal.Header>
        <Modal.Content>
          <Form className='form-custom'>
            <Form.Field>
              <Form.Input
                label="Case Number (optional)"
                name="caseRecordNumber"
                value={formValues.caseRecordNumber}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
            </Form.Field>
            <div className='required field'>
              <label>Describe the issue (paste screenshot if available)</label>
              <RichTextEditor onFocus={handleRichEditorFocus} isFocus={isEditorFocus} onChange={handleRichEditorChange} editorState={editorState} rows={6} theme={isDarkTheme ? 'dark' : 'light'} showHtml={formValues.issue} showImageUpload />
              {formErrors?.issue && <div className="error-message" style={{ color: 'red', marginTop: '1rem' }}>{formErrors?.issue}</div>}
            </div>
            <Form.Field>
              <Form.Input
                label="Has this happened before?"
                name="happenedBefore"
                value={formValues.happenedBefore}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={!!formErrors?.happenedBefore}
                required
              />
              {formErrors?.happenedBefore && <div className="error-message" style={{ color: 'red' }}>{formErrors?.happenedBefore}</div>}
            </Form.Field>
            <Form.Field className='required'>
              <label>Is this urgent?</label>
              <Form.Group inline>
                <Form.Radio
                  label='Yes'
                  value={1}
                  checked={formValues.isUrgent === true}
                  onChange={(e, data) => handleRadioButtonChange(e, data, 'isUrgent')}
                  onBlur={handleBlur}
                  style={{
                    margin: '1rem 1rem 0rem 1rem',
                    fontSize: '1.6rem'
                  }}
                />
                <Form.Radio
                  label='No'
                  value={0}
                  checked={formValues.isUrgent === false}
                  onChange={(e, data) => handleRadioButtonChange(e, data, 'isUrgent')}
                  onBlur={handleBlur}
                  style={{
                    margin: '1rem 1rem 0rem 1rem',
                    fontSize: '1.6rem'
                  }}
                />
              </Form.Group>
              {formErrors?.isUrgent && <div className="error-message" style={{ color: 'red' }}>{formErrors?.isUrgent}</div>}
            </Form.Field>
            <Form.Field>
              <Form.Input
                label="How is this impacting your work?"
                name="workImpact"
                value={formValues.workImpact}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={!!formErrors?.workImpact}
                required
              />
              {formErrors?.workImpact && <div className="error-message" style={{ color: 'red' }}>{formErrors?.workImpact}</div>}
            </Form.Field>
            <Grid>
              <Grid.Column>
                <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                  Submit
                </Button>
                <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => setIsOpenModal(false)}>
                  Cancel
                </Button>
              </Grid.Column>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default SwitchTheme;